import React, { useState, useCallback, useEffect, Suspense, useRef } from 'react'
import HeaderTitle from '_/components/header-title'
import { Grid } from '@material-ui/core'
import { Datagrid, Button } from '@refera/ui-web'
import { navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import Theme from '@refera/ui-core'

import { getInspectionBudgets } from '_/modules/inspectionBudget/actions'
import { getAgency } from '_/modules/agency/actions'
import { getInspectionBudgetSelector } from '_/modules/inspectionBudget/selector'
import { ROUTES } from '_/utils/constants'

import { BUDGET_COLUMNS, LIST_TITLE, SHOW_PROVIDERS_BUTTON, BUDGET_URL } from './utils/constants'
import {
  handleDownloadPdf,
  getInspectionBudgetPdfExportMode,
  PDF_EXPORT_MODES,
} from './utils/functions'
import { PdfExportModeModal } from './components/modals/pdf-export-mode-modal'

import { FilterSearch as FilterIcon } from '@refera/ui-icons'
import { useToast } from '_/hooks/use-toast'
import useStyles, { datagridStyle } from './styles'
import InspectionBudgetFilter from './filters'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_hooks/use-role-permission'

const AgencySelectionModal = React.lazy(() =>
  import('./components/modals/agency-selection-modal').then(module => ({
    default: module.AgencySelectionModal,
  }))
)

export default function InspectionBudgetList({ budgetType = 'inspection' }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { checkUserPermission } = useRolePermission()

  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [isPdfModeModalOpen, setIsPdfModeModalOpen] = useState(false)
  const [pendingPdfDownload, setPendingPdfDownload] = useState(null)
  const agencyExceptionCacheRef = useRef(new Map())

  const budgets = useSelector(getInspectionBudgetSelector)

  const filterIconColor = loading ? Theme.Colors.Grayscale.ThirtyTwo : Theme.Colors.Primary.Base

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterValues, setFilterValues] = useState({})
  const { showToast } = useToast()

  const handleOrderBy = orderObj => {
    const order = orderObj[0]
    if (!order) {
      setOrderBy('createdAt')
      return
    }

    const { field, sort } = order
    setOrderBy(`${sort === 'desc' ? '-' : ''}${field}`)
  }

  const handleGoToBudget = useCallback(
    event => {
      const { id } = event.row
      window.open(`${BUDGET_URL[budgetType]}/${id}`, '_blank', 'noopener,noreferrer')
    },
    [budgetType]
  )

  const handleFilterOpen = () => setFilterOpen(prev => !prev)

  const handleApplyFilter = useCallback(values => {
    setFilterValues(values)
    setPage(0)
  }, [])

  useEffect(() => {
    setLoading(true)
    const formattedFilterValues = {
      id: filterValues.searchId,
      bitrixId: filterValues.bitrixId,
      contractCode: filterValues.contractCode,
      agency: filterValues.agencyId?.value,
      tradesman: filterValues.tradesmanId?.value,
    }

    const cleanedParams = Object.entries(formattedFilterValues).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})

    dispatch(
      getInspectionBudgets({
        budget_type: budgetType,
        page: page + 1,
        pageSize,
        orderBy,
        ...cleanedParams,
      })
    )
      .catch(() => {
        showToast({ type: 'error', message: 'Ocorreu um erro ao buscar orçamentos' })
      })
      .finally(() => setLoading(false))
  }, [dispatch, page, pageSize, orderBy, filterValues])

  const handleDownload = useCallback(
    async (event, row) => {
      event.preventDefault()
      event.stopPropagation()

      if (budgetType === 'insurance') {
        const agencyId = row?.agency?.id
        let cityExceptionList = agencyExceptionCacheRef.current.get(agencyId)

        if (!cityExceptionList && agencyId) {
          try {
            const agencyData = await dispatch(getAgency(agencyId))
            cityExceptionList = agencyData?.insuranceCityExceptionList || []
            agencyExceptionCacheRef.current.set(agencyId, cityExceptionList)
          } catch (error) {
            cityExceptionList = []
          }
        }

        const defaultMode = getInspectionBudgetPdfExportMode({
          city: row?.city,
          state: row?.state,
          cityExceptionList,
        })

        setPendingPdfDownload({
          row,
          defaultMode,
          showMaximumRecommendation: defaultMode === PDF_EXPORT_MODES.MAXIMUM,
        })
        setIsPdfModeModalOpen(true)
        return
      }

      await handleDownloadPdf({
        id: row.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: row?.pdfFileSuffix,
        pdfReferenceId: row?.pdfReferenceId,
      })
    },
    [budgetType, dispatch, showToast]
  )

  const handleConfirmPdfMode = useCallback(
    async selectedMode => {
      if (!pendingPdfDownload?.row?.id) return

      await handleDownloadPdf({
        id: pendingPdfDownload.row.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: pendingPdfDownload.row?.pdfFileSuffix,
        pdfReferenceId: pendingPdfDownload.row?.pdfReferenceId,
        exportMode: selectedMode,
      })
      setIsPdfModeModalOpen(false)
      setPendingPdfDownload(null)
    },
    [dispatch, pendingPdfDownload, showToast]
  )

  const handlePdfModeModalVisibility = useCallback(isOpen => {
    setIsPdfModeModalOpen(isOpen)
    if (!isOpen) {
      setPendingPdfDownload(null)
    }
  }, [])

  return (
    <Grid className={styles.container}>
      <HeaderTitle title={LIST_TITLE[budgetType]}>
        <Grid className={styles.headerButtons}>
          {SHOW_PROVIDERS_BUTTON[budgetType] && (
            <Button
              variant="primary"
              disabled={loading}
              onClick={() => navigate(ROUTES.INSPECTION_BUDGET_PROVIDERS)}
            >
              Prestadores
            </Button>
          )}
          <Button
            variant="primary"
            disabled={loading}
            onClick={() =>
              checkUserPermission({
                permission: PERMISSIONS.INSPECTION_BUDGET,
                permissionAction: PERMISSIONS_ACTIONS.ADD,
                functionAccess: () => setOpenModal(true),
              })
            }
          >
            Novo
          </Button>
          <Button
            variant="ghost"
            startIcon={<FilterIcon color={filterIconColor} />}
            onClick={handleFilterOpen}
          >
            Filtros
          </Button>
        </Grid>
      </HeaderTitle>

      <InspectionBudgetFilter
        budgetType={budgetType}
        open={filterOpen}
        onClose={handleFilterOpen}
        onApply={handleApplyFilter}
        defaultValues={filterValues}
      />

      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <Datagrid
            rows={budgets?.results ?? []}
            rowCount={budgets?.count ?? 0}
            loading={loading}
            getRowId={row => row?.id}
            columns={BUDGET_COLUMNS(budgetType, loading, handleDownload)}
            paginationMode="server"
            onPageChange={setPage}
            page={page}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            onSortModelChange={handleOrderBy}
            sx={datagridStyle}
            onRowClick={handleGoToBudget}
          />
        </Grid>
      </Grid>
      {openModal && (
        <Suspense fallback={null}>
          <AgencySelectionModal
            budgetType={budgetType}
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        </Suspense>
      )}
      <PdfExportModeModal
        openModal={isPdfModeModalOpen}
        setOpenModal={handlePdfModeModalVisibility}
        defaultMode={pendingPdfDownload?.defaultMode}
        showMaximumRecommendation={pendingPdfDownload?.showMaximumRecommendation}
        isLoading={loading}
        onConfirm={handleConfirmPdfMode}
      />
    </Grid>
  )
}
